import { useState, useEffect } from 'react'
import type { QcConfiguration, TestType } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import {
  PageHeader,
  DataTable,
  Pagination,
  Modal,
  FormField,
  ConfirmDialog,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  btnDanger,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'
import { useTableColumns } from '../../hooks/use-table-columns'

interface Form {
  testTypeId: string
  rangeLow: string
  rangeHigh: string
  rangeUnits: string
  westgardRulesText: string
  frequencyHours: string
  notes: string
}

const defaults: Form = {
  testTypeId: '',
  rangeLow: '',
  rangeHigh: '',
  rangeUnits: '',
  westgardRulesText: '1-3s, 2-2s, R-4s',
  frequencyHours: '',
  notes: '',
}

export function QcConfigurationsPage() {
  const { data, total, page, loading, setPage, refresh } = useApiList<QcConfiguration>({ path: '/qc-configurations' })
  const { tableId: _, ...tableColumnProps } = useTableColumns('qc-configurations')
  const [testTypes, setTestTypes] = useState<readonly TestType[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState<QcConfiguration | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [form, setForm] = useState<Form>(defaults)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly TestType[]>('/test-types?isActive=true&limit=500')
      .then((res) => {
        if (cancelled) return
        if (res.data) setTestTypes(res.data)
      })
      .catch(() => {
        // non-fatal
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!modalOpen) return
    setError(null)
    if (isNew || !selected) {
      setForm(defaults)
      return
    }
    const range = (selected.acceptableRange ?? {}) as { low?: number; high?: number; units?: string }
    setForm({
      testTypeId: selected.testTypeId,
      rangeLow: range.low !== undefined ? String(range.low) : '',
      rangeHigh: range.high !== undefined ? String(range.high) : '',
      rangeUnits: range.units ?? '',
      westgardRulesText: selected.westgardRules.join(', '),
      frequencyHours: selected.frequencyHours !== null ? String(selected.frequencyHours) : '',
      notes: selected.notes ?? '',
    })
  }, [modalOpen, isNew, selected])

  const update = <K extends keyof Form>(k: K, v: Form[K]) => setForm((prev) => ({ ...prev, [k]: v }))

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const westgardRules = form.westgardRulesText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      const rangeLow = form.rangeLow ? Number(form.rangeLow) : undefined
      const rangeHigh = form.rangeHigh ? Number(form.rangeHigh) : undefined
      if ((form.rangeLow && Number.isNaN(rangeLow)) || (form.rangeHigh && Number.isNaN(rangeHigh))) {
        setError('Range values must be numeric.')
        setSaving(false)
        return
      }
      const freq = form.frequencyHours ? Number(form.frequencyHours) : undefined
      if (form.frequencyHours && (Number.isNaN(freq) || (freq ?? 0) <= 0)) {
        setError('Frequency must be a positive integer.')
        setSaving(false)
        return
      }
      const acceptableRange =
        rangeLow !== undefined || rangeHigh !== undefined || form.rangeUnits
          ? {
              ...(rangeLow !== undefined ? { low: rangeLow } : {}),
              ...(rangeHigh !== undefined ? { high: rangeHigh } : {}),
              ...(form.rangeUnits ? { units: form.rangeUnits } : {}),
            }
          : undefined
      const payload: Record<string, unknown> = { westgardRules }
      if (acceptableRange) payload.acceptableRange = acceptableRange
      if (freq !== undefined) payload.frequencyHours = freq
      if (form.notes) payload.notes = form.notes
      if (isNew) {
        payload.testTypeId = form.testTypeId
        await api.post<QcConfiguration>('/qc-configurations', payload)
      } else {
        await api.put<QcConfiguration>(`/qc-configurations/${selected?.id}`, payload)
      }
      refresh()
      setModalOpen(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!selected) return
    setArchiving(true)
    try {
      await api.delete(`/qc-configurations/${selected.id}`)
      refresh()
      archiveModal.close()
      setModalOpen(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  const testTypeById = new Map(testTypes.map((tt) => [tt.id, tt]))

  const columns: readonly Column<QcConfiguration>[] = [
    {
      key: 'testType',
      header: 'Test Type',
      sortable: true,
      render: (c) => {
        const tt = testTypeById.get(c.testTypeId)
        return tt ? `${tt.code} - ${tt.name}` : c.testTypeId.slice(0, 8)
      },
    },
    {
      key: 'range',
      header: 'Acceptable Range',
      render: (c) => {
        const r = (c.acceptableRange ?? {}) as { low?: number; high?: number; units?: string }
        if (r.low === undefined && r.high === undefined) return '--'
        const unit = r.units ? ` ${r.units}` : ''
        return `${r.low ?? ''}${r.low !== undefined && r.high !== undefined ? '-' : ''}${r.high ?? ''}${unit}`
      },
    },
    { key: 'rules', header: 'Westgard Rules', render: (c) => c.westgardRules.join(', ') || '--' },
    { key: 'freq', header: 'Frequency (h)', render: (c) => c.frequencyHours ?? '--' },
    { key: 'status', header: 'Status', sortable: true, render: (c) => (c.isActive ? 'Active' : 'Archived') },
  ]

  const selectedTestType = testTypeById.get(form.testTypeId)
  const title = isNew ? 'New QC Configuration' : selectedTestType ? `${selectedTestType.code} QC Config` : 'QC Config'

  return (
    <div>
      <PageHeader
        title="QC Configurations"
        subtitle={`${total} configured`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button
              type="button"
              onClick={() => {
                setSelected(null)
                setIsNew(true)
                setModalOpen(true)
              }}
              style={btnPrimary}
            >
              Add Configuration
            </button>
          </>
        }
      />
      <p style={{ color: colors.textMuted, fontSize: '0.875rem', margin: '0 0 1rem' }}>
        Per-test-type QC rules. Westgard rule shorthand -- common rules: <code>1-3s</code>, <code>1-2s</code>,{' '}
        <code>2-2s</code>, <code>R-4s</code>, <code>4-1s</code>, <code>10x</code>. Comma-separated.
      </p>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(c) => {
              setSelected(c)
              setIsNew(false)
              setModalOpen(true)
            }}
            emptyMessage="No QC configurations yet. Add one per test type to drive the evaluator."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={title} width="720px">
        <div>
          <FormField name="testTypeId" label="Test Type" required>
            {isNew ? (
              <select style={input} value={form.testTypeId} onChange={(e) => update('testTypeId', e.target.value)}>
                <option value="">-- select --</option>
                {testTypes.map((tt) => (
                  <option key={tt.id} value={tt.id}>
                    {tt.code} - {tt.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                style={input}
                value={selectedTestType ? `${selectedTestType.code} - ${selectedTestType.name}` : form.testTypeId}
                disabled
              />
            )}
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
            <FormField
              name="rangeLow"
              label="Range Low"
              value={form.rangeLow}
              onChange={(v) => update('rangeLow', v)}
            />
            <FormField
              name="rangeHigh"
              label="Range High"
              value={form.rangeHigh}
              onChange={(v) => update('rangeHigh', v)}
            />
            <FormField
              name="rangeUnits"
              label="Units"
              value={form.rangeUnits}
              onChange={(v) => update('rangeUnits', v)}
            />
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField
              name="westgardRulesText"
              label="Westgard Rules (comma-separated)"
              value={form.westgardRulesText}
              onChange={(v) => update('westgardRulesText', v)}
            />
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField
              name="frequencyHours"
              label="Run Frequency (hours, optional)"
              value={form.frequencyHours}
              onChange={(v) => update('frequencyHours', v)}
            />
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField name="notes" label="Notes">
              <textarea
                style={{ ...input, minHeight: '60px', resize: 'vertical' }}
                value={form.notes}
                onChange={(e) => update('notes', e.target.value)}
              />
            </FormField>
          </div>
          {error && (
            <div
              style={{
                marginTop: '0.75rem',
                color: colors.danger,
                padding: '0.5rem 0.75rem',
                background: colors.bgError,
                border: `1px solid ${colors.borderError}`,
                borderRadius: '3px',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
            {!isNew && (
              <button
                type="button"
                onClick={archiveModal.open}
                style={{ ...btnDanger, marginRight: 'auto' }}
                disabled={saving || archiving}
              >
                Archive
              </button>
            )}
            <button type="button" onClick={() => setModalOpen(false)} style={btnSecondary} disabled={saving}>
              Cancel
            </button>
            <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive QC Configuration"
        message="Archive this QC configuration? Existing QC runs are preserved; the evaluator falls back to defaults."
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
